import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePanelContainer } from '../rendering/Panels/usePanelContainer';
import { useAnalysisPage } from '../useAnalysisPage';
import styles from './SegmentManagerList.module.scss';
import {
  EditNames,
  SegmentationControllerType,
  getModificationAnalysisType,
  getSegShapeAnalysisType,
  getSegmentationAnalysisType,
} from 'config/analysisConfig';
import { fetchSeriesArrayBuffer } from '@api/files';
import useAuth from '@hooks/Auth/useAuth';
import {
  ContextPool,
  CPRBufferKeys,
  SurfaceContext,
  update2DLabel,
  useBufferPool,
} from '../rendering/Panels/Renderers/vtkManagers';
import {
  applyConnect2DSelection,
  clearConnect2DSelection,
  resetViewport,
  subscribeConnect2DSelection,
  updateManualSegColor,
} from '../rendering/Panels/Renderers/CornerstoneManager';
import type { Connect2DSelectionState } from '../rendering/Panels/Renderers/CornerstoneManager';
import { updateRendererActorsColor } from '../segmentManager/core/SegmentManagerCore';
import {
  applySegmentManager,
  createSegmentManagerDraft,
  discardSegmentManager,
  isInitialPointInSegmentation,
  ModifierType,
  redoStroke,
  restoreSegmentManagerDraft,
  undoStroke,
  useSegmentManager,
} from '../segmentManager';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import './SegmentManagerContent.scss';
import useModalActions from '@hooks/common/useModalAction';
import { TOOL_REGISTRY } from 'config/toolRegistry';
import { useTool } from '@hooks/Tool/useTool';
import { ToolId } from 'config/toolDefinitionsConfig';
import { useNavigate } from 'react-router-dom';
import { useConfig } from 'config/useConfig';
import { configStore } from 'config/configStore';
import { useTextTooltip } from '@components/Tooltip/useMouseTooltip';
import ColorPickerContextMenu from '@features/analysis/segmentManager/components/ColorPicker/ColorPickerContextMenu';
import SegmentationVisibilityList from './components/SegmentationVisibilityList';
import TaviManualPanel from './components/TaviManualPanel';
import RegulatorConnectPanel from './components/RegulatorConnectPanel';
import SegmentationShapeControlPanel from './components/SegmentationShapeControlPanel';
import ModificationToolPanel from './components/ModificationToolPanel';
import ModificationClosePanel from './components/ModificationClosePanel';
import { segmentManagerIcons as icons } from './components/segmentManagerIcons';
import { useCloseModificationMode } from './hooks/useCloseModificationMode';
import { useCprModificationHistoryStatus } from './hooks/useCprModificationHistoryStatus';
import { useCprModificationSettings } from './hooks/useCprModificationSettings';
import { useManualSegmentationController } from './hooks/useManualSegmentationController';
import { useModificationModeLifecycle } from './hooks/useModificationModeLifecycle';
import { useRegulatorConnectController } from './hooks/useRegulatorConnectController';
import { useSegmentationShapeController } from './hooks/useSegmentationShapeController';
import { useSegmentationVisibilityController } from './hooks/useSegmentationVisibilityController';
import { EDIT_WARNING_MESSAGE } from '@features/view/Modification';
import { createHandlers, segmentManagerStore } from './components/segmentManagerStore';
import { getIsSegmentedRegionSelected } from './hooks/useEditModeConfig';
import {
  buildCTFFREditDraftKey,
  deleteCTFFREditDraftsFor,
  findCTFFREditDraft,
  putCTFFREditDraft,
  type CTFFREditDraft,
} from './editDraftStorage';

export async function exportNIfTI(
  accessToken: string,
  seriesId: string,
  segmentKey: SegmentationControllerType,
) {
  let filePath = '';
  switch (segmentKey) {
    case 'la':
      filePath = 'heart_la';
      break;
    case 'laWall':
      filePath = 'heart_la_wall';
      break;
    case 'lv':
      filePath = 'heart_lv';
      break;
    case 'lvWall':
      filePath = 'heart_lv wall';
      break;
    case 'ra':
      filePath = 'heart_ra';
      break;
    case 'raWall':
      filePath = 'heart_ra wall';
      break;
    case 'rv':
      filePath = 'heart_rv';
      break;
    case 'rvWall':
      filePath = 'heart_rv wall';
      break;
    case 'aorta':
      filePath = 'aorta';
      break;
    case 'coronary':
      filePath = 'lumen';
      break;
    case 'epicardial':
      filePath = 'epicardial';
      break;
  }

  if (filePath === '') return false;
  filePath += '.nii.gz';
  const { data, status } = await fetchSeriesArrayBuffer({
    accessToken,
    seriesId,
    target: filePath,
  });

  if (status !== 200) return false;

  const res = await window.electron.ipcRenderer.saveFile(data, filePath);
  console.debug(res);
  return true;
}

function convertModiType(activeToolId?: ToolId) {
  switch (activeToolId) {
    case 'Brush':
      return ModifierType.BRUSH;
    case 'Eraser':
      return ModifierType.ERASER;
    case 'FloodFill':
      return ModifierType.PREVIEW;
    case 'Connect2D':
      return ModifierType.CONNECT_2D;
    case 'connectPoint':
      return ModifierType.POINT;
    default:
      return ModifierType.IDLE;
  }
}

export function SegmentManagerList(params: {
  contextPool: ContextPool;
  analysisStatusByName?: Map<string, boolean | null>;
}) {
  const HIDE_TAVI_MANUAL_TEMP = true;
  const { accessToken } = useAuth();
  const {
    selectedSeriesId,
    globalToolState,
    panelToolState,
    selectedLayoutConfig,
    resetSelectedSeries,
    updateCurrentLayout,
    updateSelectAnalysis,
    handleUpdateGlobalTool,
  } = useAnalysisPage();
  const { selectedSeries, selectedSeriesImageTag } = useBrowserPage();
  const {
    updatePanelState,
    panelStates,
    analysisType,
    selectedPanelComponentKey,
  } = usePanelContainer();
  const { config, setData } = useConfig();
  const { bufferPool, clearPool } = useBufferPool();
  const {
    selectAction,
    waitingAction,
    closeLoadingModal,
    errorDefaultAction,
    successAction,
  } = useModalActions();
  const navigate = useNavigate();
  const { getVisibleToolsForContext, handleToolActivate } = useTool();
  const {
    mode: manualMode,
    dirty,
    open,
    setMode,
    setDirty,
    setCanApplyModification,
  } = useSegmentManager();
  const {
    tooltipNode,
    tooltipHandlers,
  } = useTextTooltip();

  const { segmentation } = panelStates['Surface3D'];
  if (!segmentation) return <></>;

  const analysisStatusByName = params.analysisStatusByName ?? new Map();
  const segmentationReady = analysisStatusByName.get('segmentation') === true;
  const coronaryReady = analysisStatusByName.get('coronary') === true;
  const pool = selectedSeriesId ? bufferPool[selectedSeriesId] : undefined;

  const [taviManualMode, setTaviManualMode] = useState(false);
  const [undoable, setUndoable] = useState(dirty);
  const [redoable, setRedoable] = useState(false);
  const [connect2DStatus, setConnect2DStatus] = useState<Connect2DSelectionState>({
    start: null,
    end: null,
    ready: false,
  });
  const [connect2DApplying, setConnect2DApplying] = useState(false);

  const hasVR = selectedLayoutConfig.panelConfig.some(
    (panel) => panel.componentKey === 'VR',
  );
  const lowQuality = config.generalSetting.renderingQuality !== 'high';
  const showKeys = useMemo(() => {
    if (!analysisType) return [];
    const keys = getSegmentationAnalysisType(
      analysisType,
    ) as SegmentationControllerType[];
    return analysisType === 'FFR' ? keys.filter((key) => key !== 'aorta') : keys;
  }, [analysisType, segmentation]);

  const visibleTools = getVisibleToolsForContext();
  const visibleToolIds = visibleTools.map((tool) => tool.id);
  const invisibleToolIds = useMemo(
    () => TOOL_REGISTRY.map((tool) => tool.id).filter(
      (id) => !visibleToolIds.includes(id),
    ),
    [visibleToolIds],
  );

  const showCprModificationTools =
    analysisType === 'FFR' && config.EditMode === EditNames.lumen;
  const showRegulatorConnectTools =
    analysisType === 'FFR' &&
    (config.EditMode === EditNames.regulator || config.EditMode === EditNames.lumen);
  const showConnect2DTools =
    analysisType === 'FFR' && config.EditMode === EditNames.lumen;
  const showConnectToolPanel = (showRegulatorConnectTools || showConnect2DTools);
  const isSegLabelsVisible = analysisType === 'FFR' && config.EditMode !== null;
  const isModifyToolMode = getModificationAnalysisType(analysisType) && !hasVR;

  const isSegmentedRegionSelected = !getIsSegmentedRegionSelected();

  const [isManual3DVisible, setIsManual3DVisible] = useState(
    isSegmentedRegionSelected,
  );

  const isMIPEnabled =
    config.visibleMIP && getSegShapeAnalysisType(analysisType);

  const isReady = Object.values(config.viewportSwitchPendingMap).every(
    Boolean,
  );
  const prevReadyRef = useRef(isReady);
  const modifierType = useMemo(() => {
    if (!isReady) {
      return ModifierType.IDLE;
    }
    return convertModiType(panelToolState?.activeToolId);
  }, [isReady, panelToolState?.activeToolId]);

  const regulatorConnect = useRegulatorConnectController({
    config,
    setData,
    selectedSeriesId,
    lowQuality,
    manualMode,
    showRegulatorConnectTools,
    handleToolActivate,
    setMode,
    setDirty,
    modifierType,
    activeToolId : panelToolState?.activeToolId,
    enabled: panelToolState?.activeToolId === 'connectPoint'
  });



  useEffect(() => {
    const wasReady = prevReadyRef.current;
    prevReadyRef.current = isReady;
    if (!wasReady && isReady && config.EditMode) {
      requestAnimationFrame(() => {
        resetViewport(
          false,
          globalToolState?.toggles.ToggleDicomZoomSync ?? true,
        );
      });
    }
  }, [isReady]);


  const {
    cprModificationTool,
    cprSculptSize,
    cprPixelBrushSize,
    cprPixelBrushMode,
    handleCprModificationToolToggle,
    handleCprSculptSizeChange,
    handleCprPixelBrushSizeChange,
    handleCprPixelBrushModeChange,
  } = useCprModificationSettings({ config, setData });

  const {
    selectedKey,
    sorted,
    colorOptions,
    colorMenuOpen,
    handleSetSelectedKey,
    handleColorPick,
    handleToggleShow,
    handleColorMenuToggle,
    handleColorMenuClose,
  } = useSegmentationVisibilityController({
    segmentation,
    showKeys,
    selectedSeriesId,
    manualMode,
    hasVR,
    analysisType,
    updatePanelState,
  });

  const {
    brushStep,
    startValue,
    endValue,
    HUMin,
    HUMax,
    handleBrushThresholdChange,
    handleBrushStepChange,
    handleManualEnter,
    handleManualExit,
    handleManualToolEnter,
  } = useManualSegmentationController({
    segmentation,
    selectedKey,
    handleSetSelectedKey,
    selectedSeriesId,
    selectedSeriesPixelSpacing: selectedSeriesImageTag?.pixelSpacing,
    contextPoolSurface3D: params.contextPool.Surface3D,
    selectedPanelComponentKey,
    lowQuality,
    manualMode,
    dirty,
    modifierType,
    handleToolActivate,
    onDisableRegulatorConnect: regulatorConnect.disableAndClear,
    setUndoable,
    setRedoable,
    setMode,
    setDirty,
  });

  useModificationModeLifecycle({
    enabled: analysisType === 'FFR',
    editMode: config.EditMode,
    selectedSeriesId,
    lowQuality,
    manualMode,
    handleManualEnter,
    handleManualExit,
    setDirty,
  });

  const {
    isSegmentationShape,
    modes,
    toggleSegmentationGroup,
    toggleVolumeColorType,
    handleToggleHeartCutTool,
    handleToggleMipTool,
    handlelightingChange,
  } = useSegmentationShapeController({
    analysisType,
    config,
    setData,
    segmentation,
    segmentationReady,
    coronaryReady,
    hasVR,
    updatePanelState,
    waitingAction,
    closeLoadingModal,
    handleToolActivate,
  });

  const isSelectedCprPanel = selectedPanelComponentKey === 'CPR';
  const isSelected2DPanel =
    selectedPanelComponentKey === 'Axial' ||
    selectedPanelComponentKey === 'Coronal' ||
    selectedPanelComponentKey === 'Sagittal';
  const isSelectedSegmentHistoryPanel =
    isSelected2DPanel ||
    isSelectedCprPanel ||
    selectedPanelComponentKey === 'Surface3D';
  useEffect(() => subscribeConnect2DSelection(setConnect2DStatus), []);

  // useEffect(() => {
  //   if (
  //     (modifierType !== ModifierType.CONNECT_2D) &&
  //     (connect2DStatus.start || connect2DStatus.end || connect2DStatus.message)
  //   ) {
  //     clearConnect2DSelection();
  //   }
  // }, [
  //   connect2DStatus.end,
  //   connect2DStatus.message,
  //   connect2DStatus.start,
  // ]);

  const handleConnect2DApply = useCallback(async () => {
    if (!selectedSeriesId || connect2DApplying) return;

    setConnect2DApplying(true);
    waitingAction({ message: 'Applying Connect 2D...' });

    try {
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      await applyConnect2DSelection({
        seriesId: selectedSeriesId,
        lowQuality,
      });
    } finally {
      closeLoadingModal();
      setConnect2DApplying(false);
    }
  }, [closeLoadingModal, connect2DApplying, lowQuality, selectedSeriesId, waitingAction]);

  const handleConnect2DClear = useCallback(() => {
    clearConnect2DSelection();
  }, []);

  const handleConnect2DToolToggle = useCallback(() => {
    if (connect2DApplying) return;

    if(config.EditMode === 'regulator') return;

    const connect2DActive =
      modifierType === ModifierType.CONNECT_2D ||
      panelToolState?.activeToolId === 'Connect2D';

    if (connect2DActive) {
      clearConnect2DSelection();
      handleToolActivate('Scroll');
      setMode(true);
      return;
    }

    handleManualToolEnter('Connect2D');
  }, [
    connect2DApplying,
    handleManualToolEnter,
    handleToolActivate,
    modifierType,
    panelToolState?.activeToolId,
    setMode,
  ]);

  const {
    cprHistoryStatus,
    canUndoSelectedModification,
    canRedoSelectedModification,
    handleUndoSelectedModification,
    handleRedoSelectedModification,
  } = useCprModificationHistoryStatus({
    isSelectedCprPanel,
    isSelectedSegmentHistoryPanel,
    undoable,
    redoable,
    undoStroke,
    redoStroke,
  });
  const canApplyModification =
    undoable || (showCprModificationTools && cprHistoryStatus.canUndo);

  const editDraftKey = useMemo(
    () =>
      buildCTFFREditDraftKey({
        seriesId: selectedSeriesId,
        editMode: config.EditMode,
        renderingQuality: config.generalSetting.renderingQuality,
      }),
    [
      config.EditMode,
      config.generalSetting.renderingQuality,
      selectedSeriesId,
    ],
  );
  const handleSaveEditDraft = useCallback(async () => {
    if (!editDraftKey || !selectedSeriesId || !config.EditMode || !manualMode) {
      return;
    }

    const snapshot = createSegmentManagerDraft();
    if (!snapshot) {
      await errorDefaultAction('There is no edit data to save.');
      return;
    }

    const draft: CTFFREditDraft = {
      ...snapshot,
      key: editDraftKey,
      savedAt: Date.now(),
      seriesId: selectedSeriesId,
      editMode: config.EditMode,
      renderingQuality: config.generalSetting.renderingQuality,
      uiState: {
        regulatorConnectThickness: config.regulatorConnectThickness,
        regulatorConnectHuEnabled: config.regulatorConnectHuEnabled,
        regulatorConnectHuMin: config.regulatorConnectHuMin,
        regulatorConnectHuMax: config.regulatorConnectHuMax,
        cprModificationTool,
        cprPixelBrushMode,
        cprPixelBrushSize,
        cprSculptSize,
      },
    };

    await putCTFFREditDraft(draft);
    await successAction('Edit data has been saved locally.');
  }, [
    config.EditMode,
    config.generalSetting.renderingQuality,
    config.regulatorConnectHuEnabled,
    config.regulatorConnectHuMax,
    config.regulatorConnectHuMin,
    config.regulatorConnectThickness,
    cprModificationTool,
    cprPixelBrushMode,
    cprPixelBrushSize,
    cprSculptSize,
    editDraftKey,
    errorDefaultAction,
    manualMode,
    selectedSeriesId,
    successAction,
  ]);

  const restoreEditDraft = useCallback(async (draft: CTFFREditDraft) => {
    const restored = restoreSegmentManagerDraft(draft);
    if (!restored) {
      await errorDefaultAction('Failed to load saved edit data.');
      return false;
    }

    if (draft.uiState) {
      setData({
        regulatorConnectThickness: draft.uiState.regulatorConnectThickness,
        regulatorConnectHuEnabled: draft.uiState.regulatorConnectHuEnabled,
        regulatorConnectHuMin: draft.uiState.regulatorConnectHuMin,
        regulatorConnectHuMax: draft.uiState.regulatorConnectHuMax,
        cprModificationTool: draft.uiState.cprModificationTool as any,
        cprPixelBrushMode: draft.uiState.cprPixelBrushMode as any,
        cprPixelBrushSize: draft.uiState.cprPixelBrushSize,
        cprSculptSize: draft.uiState.cprSculptSize,
      });
    }

    setDirty(true);
    setUndoable(draft.undoStack.length > 0 || draft.externalDeltas.length > 0);
    setRedoable(draft.redoStack.length > 0);
    return true;
  }, [errorDefaultAction, setData, setDirty]);

  const handleLoadEditDraft = useCallback(async () => {
    if (!selectedSeriesId || !config.EditMode || analysisType !== 'FFR') {
      await errorDefaultAction('Edit draft can only be loaded in CT-FFR edit mode.');
      return;
    }

    try {
      const draft = await findCTFFREditDraft({
        key: editDraftKey,
        seriesId: selectedSeriesId,
        editMode: config.EditMode,
        renderingQuality: config.generalSetting.renderingQuality,
      });

      if (!draft) {
        await errorDefaultAction('No saved edit data found for this series and edit mode.');
        return;
      }

      const loaded = await restoreEditDraft(draft);
      if (loaded) {
        await successAction('Saved edit data has been loaded.');
      }
    } catch (error) {
      console.warn('[CT-FFR Edit Draft] Failed to load saved draft.', error);
      await errorDefaultAction('Failed to load saved edit data.');
    }
  }, [
    analysisType,
    config.EditMode,
    config.generalSetting.renderingQuality,
    editDraftKey,
    errorDefaultAction,
    restoreEditDraft,
    selectedSeriesId,
    successAction,
  ]);

  useEffect(() => {
    if (!manualMode || !config.EditMode || analysisType !== 'FFR') return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey)) return;
      if (event.key.toLowerCase() !== 's') return;

      event.preventDefault();
      event.stopPropagation();
      void handleSaveEditDraft();
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [analysisType, config.EditMode, handleSaveEditDraft, manualMode]);

  useEffect(() => {
    setCanApplyModification(canApplyModification);
  }, [canApplyModification, setCanApplyModification]);

  useEffect(() => {
    return () => {
      setCanApplyModification(false);
    };
  }, [setCanApplyModification]);

  const handleCloseModificationMode = useCloseModificationMode({
    analysisType,
    selectedSeries,
    selectedSeriesId,
    globalToolState,
    handleManualExit,
    handleUpdateGlobalTool,
    updateCurrentLayout,
    updateSelectAnalysis,
    disableRegulatorConnect: regulatorConnect.disableAndClear,
    setDirty,
  });
  const handleCloseModificationModeClick = useCallback(() => {
    if (!canApplyModification) {
      handleCloseModificationMode();
      return;
    }

    selectAction(
      EDIT_WARNING_MESSAGE,
      handleCloseModificationMode,
      { right: 'Confirm' },
      'compact-warning-modal',
    );
  }, [canApplyModification, handleCloseModificationMode, selectAction]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    (window as any).__taviManualMode = taviManualMode;
    window.dispatchEvent(
      new CustomEvent('tavi-manual-mode-changed', {
        detail: { enabled: taviManualMode },
      }),
    );
  }, [taviManualMode]);

  // const handleDiscardManualChanges = useCallback(() => {
  //   discardSegmentManager(setDirty);
  //   if (config.EditMode && analysisType === 'FFR') return;
  //   handleManualExit();
  // }, [analysisType, config.EditMode, handleManualExit]);

  const handleApplyAndExit = useCallback(async () => {
    if (!selectedSeriesId) return;

    if (isInitialPointInSegmentation(selectedSeriesId)) {
      await errorDefaultAction('Invalid initial point location. Please check and try again.');
      return;
    }
    const res = await applySegmentManager(accessToken, selectedSeriesId, config.EditMode === 'regulator');
    if (res) {
      await deleteCTFFREditDraftsFor({
        seriesId: selectedSeriesId,
        editMode: config.EditMode,
      });
      handleManualExit();
      requestAnimationFrame(() => {
        clearPool();
        setData({shouldRunFFR:true});
        resetSelectedSeries();
        navigate('/browser');
      });
    }
  }, [
    accessToken,
    clearPool,
    config.EditMode,
    editDraftKey,
    handleManualExit,
    navigate,
    resetSelectedSeries,
    selectedSeriesId,
    errorDefaultAction,
  ]);

  const handleOpenManualApply = useCallback(() => {
    open({
      message: 'Apply changes?',
      cancelCallback: () => {
        console.log('cancel apply');
      },
      applyCallback: handleApplyAndExit,
    });
  }, [handleApplyAndExit, open]);

   //Connect2D Apply 핸들러를 전역 Store에 등록
  useEffect(() => {
    Object.assign(segmentManagerStore.connect, {
      apply: panelToolState?.activeToolId === 'connectPoint'
        ? regulatorConnect.handleApply
        : handleConnect2DApply,
      clear: panelToolState?.activeToolId === 'connectPoint'
        ? regulatorConnect.clearSelection
        : handleConnect2DClear,
      toggle: (type: 'connect2D' | 'point') => {
        if (type === 'point') {
          regulatorConnect.handleToggle();
          return;
        }

        handleConnect2DToolToggle();
      },
    });

    return () => {
      Object.assign(segmentManagerStore.connect, createHandlers());
    };
  }, [
    handleConnect2DApply,
    handleConnect2DClear,
    handleConnect2DToolToggle,
    regulatorConnect.handleApply,
    regulatorConnect.clearSelection,
    regulatorConnect.handleToggle,
  ]);

  const parseManualColorInput = (event: React.ChangeEvent<HTMLInputElement>) =>
    [
      parseInt(event.target.value.slice(1, 3), 16),
      parseInt(event.target.value.slice(3, 5), 16),
      parseInt(event.target.value.slice(5, 7), 16),
    ] as [number, number, number];

  const handleManualColorChange = useCallback(
    (
      target: '2D' | '3D',
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const rgb = parseManualColorInput(event);

      if (target === '2D') {
        setData((prev) => ({
          manualColors: { ...prev.manualColors, '2D': rgb },
        }));
        updateManualSegColor(selectedSeriesId, rgb);
        return;
      }

      setData((prev) => ({
        manualColors: { ...prev.manualColors, '3D': rgb },
      }));
      updateRendererActorsColor(
        params.contextPool.Surface3D as SurfaceContext,
        rgb,
        isManual3DVisible,
      );
    },
    [isManual3DVisible, params.contextPool.Surface3D, selectedSeriesId, setData],
  );

  const handleManualToggle = useCallback((target: '2D' | '3D') => {
    if (target === '2D') {
      update2DLabel();
      return;
    }

    const nextVisible = !isManual3DVisible;
    setIsManual3DVisible(nextVisible);
    updateRendererActorsColor(
      params.contextPool.Surface3D as SurfaceContext,
      configStore.get().manualColors['3D'],
      nextVisible,
    );
  }, [isManual3DVisible, params.contextPool.Surface3D]);

  return (
    <div className={styles.column}>
      {
        <>
          <div className={styles.column1}>
            <SegmentationVisibilityList
              sorted={sorted}
              pool={pool as Partial<Record<CPRBufferKeys, { data?: unknown }>>}
              selectedKey={selectedKey}
              manualColors={config.manualColors}
              manualMode={manualMode}
              hasVR={hasVR}
              segmentationReady={segmentationReady}
              coronaryReady={coronaryReady}
              isSegmentedRegionSelected={isSegmentedRegionSelected}
              isManual3DVisible={isManual3DVisible}
              mipEnabled={isMIPEnabled && hasVR}
              ShowIcon={icons.Show}
              HideIcon={icons.Hide}
              onSelectKey={handleSetSelectedKey}
              onToggleShow={handleToggleShow}
              onColorMenuToggle={handleColorMenuToggle}
              onManualColorChange={handleManualColorChange}
              onManualToggle={handleManualToggle}
              tooltipHandlers={tooltipHandlers}
            />

            {colorMenuOpen && (
              <ColorPickerContextMenu
                Colors={colorOptions}
                onClose={handleColorMenuClose}
                onPick={handleColorPick}
                selectedColor={segmentation[selectedKey].color}
                selectedKey={selectedKey}
              />
            )}
          </div>

          {tooltipNode}
        </>
}

      {String(analysisType) === 'tavi' && !HIDE_TAVI_MANUAL_TEMP && (
        <TaviManualPanel
          enabled={taviManualMode}
          onToggle={() => setTaviManualMode((prev) => !prev)}
        />
      )}

      {showConnectToolPanel && isSelected2DPanel && (
        <RegulatorConnectPanel
          showRegulatorConnectTools={showRegulatorConnectTools}
          showConnect2DTools={showConnect2DTools}
          enabled={panelToolState?.activeToolId === 'connectPoint'}
          hasVR={hasVR}
          isSelected2DPanel={isSelected2DPanel}
          activeToolId={panelToolState?.activeToolId}
          modifierType={modifierType}
          status={regulatorConnect.status}
          connect2DStatus={connect2DStatus}
          connect2DApplying={connect2DApplying}
          brushStep={brushStep}
          thickness={regulatorConnect.thickness}
          thicknessText={regulatorConnect.thicknessText}
          huEnabled={regulatorConnect.huEnabled}
          huMinText={regulatorConnect.huMinText}
          huMaxText={regulatorConnect.huMaxText}
          icons={icons}
          onToggle={regulatorConnect.handleToggle}
          onApply={regulatorConnect.handleApply}
          onClear={regulatorConnect.clearSelection}
          onConnect2DToolToggle={handleConnect2DToolToggle}
          onConnect2DApply={handleConnect2DApply}
          onConnect2DClear={handleConnect2DClear}
          onBrushStepChange={handleBrushStepChange}
          onThicknessChange={regulatorConnect.handleThicknessChange}
          onThicknessTextChange={regulatorConnect.handleThicknessTextChange}
          onThicknessBlur={regulatorConnect.handleThicknessBlur}
          onHuEnabledChange={regulatorConnect.handleHuEnabledChange}
          onHuRangeTextChange={regulatorConnect.handleHuRangeTextChange}
          onHuRangeBlur={regulatorConnect.normalizeHuRange}
          onInputPointerDown={regulatorConnect.stopInputPropagation}
          onInputMouseDown={regulatorConnect.stopInputPropagation}
          onInputClick={regulatorConnect.stopInputPropagation}
          onInputKeyDown={regulatorConnect.stopInputPropagation}
          onInputWheel={regulatorConnect.handleInputWheel}
          tooltipHandlers={tooltipHandlers}
        />
      )}

      {!manualMode && (
        <SegmentationShapeControlPanel
          modes={modes}
          hasVR={hasVR}
          isSegmentationShape = {isSegmentationShape}
          segmentationReady={segmentationReady}
          coronaryReady={coronaryReady}
          invisibleToolIds={invisibleToolIds}
          mipToggleActive={config.visibleMIP}
          vrType={config.vrType}
          tooltipHandlers={tooltipHandlers}
          config={config}
          handlelightingChange ={handlelightingChange}
          onToggleHeartCut={handleToggleHeartCutTool}
          onToggleSegmentationGroup={toggleSegmentationGroup}
          onToggleMIP={handleToggleMipTool}
          onToggleVolumeColorType={toggleVolumeColorType}
        />
      )}

      {isModifyToolMode && (
        <ModificationToolPanel
          manualMode={manualMode}
          showCprModificationTools={showCprModificationTools}
          canUndoSelectedModification={canUndoSelectedModification}
          canRedoSelectedModification={canRedoSelectedModification}
          canApplyModification={canApplyModification}
          canSaveDraft={undoable}
          modifierType={modifierType}
          activeToolId={panelToolState?.activeToolId}
          invisibleToolIds={invisibleToolIds}
          hasVR={hasVR}
          isSelected2DPanel={isSelected2DPanel}
          selectedPanelComponentKey={selectedPanelComponentKey}
          brushStep={brushStep}
          startValue={startValue}
          endValue={endValue}
          huMin={HUMin}
          huMax={HUMax}
          cprModificationTool={cprModificationTool}
          manual3DTool ={config.manual3DTool}
          cprSculptSize={cprSculptSize}
          cprPixelBrushMode={cprPixelBrushMode}
          cprPixelBrushSize={cprPixelBrushSize}
          connect2DStatus={connect2DStatus}
          icons={icons}
          apply3D={config.manual3DApply}
          onUndo={handleUndoSelectedModification}
          onRedo={handleRedoSelectedModification}
          onManualToolEnter={handleManualToolEnter}
          onBrushStepChange={handleBrushStepChange}
          onBrushThresholdChange={handleBrushThresholdChange}
          onOpenManualDiscard={handleCloseModificationModeClick}
          onOpenManualApply={handleOpenManualApply}
          onSaveDraft={handleSaveEditDraft}
          onLoadDraft={handleLoadEditDraft}
          onCprModificationToolToggle={handleCprModificationToolToggle}
          onCprSculptSizeChange={handleCprSculptSizeChange}
          onCprPixelBrushModeChange={handleCprPixelBrushModeChange}
          onCprPixelBrushSizeChange={handleCprPixelBrushSizeChange}
          tooltipHandlers={tooltipHandlers}
        />
      )}
    </div>
  );
}
